import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CreditCard, Shield, HeartPulse, BookOpen, Tag, Briefcase, Lock, Coins,
  ArrowRight, Loader2, ShieldCheck, Search, Sparkles, TrendingUp, Dumbbell,
  Play, Plane, Utensils, Home, PawPrint, GraduationCap, Star, Zap, User, Heart, Globe, Crown,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  "credit-card": CreditCard, "shield": Shield, "heart-pulse": HeartPulse,
  "book": BookOpen, "tag": Tag, "briefcase": Briefcase,
  "trending-up": TrendingUp, "dumbbell": Dumbbell, "play": Play,
  "plane": Plane, "utensils": Utensils, "home": Home,
  "paw-print": PawPrint, "graduation-cap": GraduationCap, "sparkles": Sparkles,
};

const AUDIENCES = [
  { id: "all", label: "Everyone", icon: Star },
  { id: "women", label: "Women", icon: Heart },
  { id: "men", label: "Men", icon: User },
  { id: "business", label: "Business & Owners", icon: Briefcase },
  { id: "students", label: "Students", icon: GraduationCap },
  { id: "young", label: "Young Pros", icon: Zap },
  { id: "foreign", label: "Foreign", icon: Globe },
  { id: "luxury", label: "Luxury", icon: Crown },
  { id: "neutral", label: "Neutral", icon: BookOpen },
];

export default function Benefits() {
  const email = getStoredEmail();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [activeCat, setActiveCat] = useState("");  // empty = all
  const [audience, setAudience] = useState("all");
  const [search, setSearch] = useState("");
  const [exclusiveOnly, setExclusiveOnly] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    const params = { audience: audience !== "all" ? audience : null };
    if (email) params.email = email;
    if (activeCat) params.category = activeCat;
    if (search) params.search = search;
    if (exclusiveOnly) params.exclusive_only = true;
    Object.keys(params).forEach(k => params[k] == null && delete params[k]);
    axios.get(`${API}/benefits`, { params })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [email, audience, activeCat, search, exclusiveOnly]);

  const click = async (offer) => {
    try {
      const r = await axios.post(`${API}/benefits/click`, {
        email: email || null, offer_id: offer.id, source_page: "/benefits",
      });
      window.open(r.data.redirect_url, "_blank", "noopener,noreferrer");
    } catch {
      window.open(offer.url, "_blank", "noopener,noreferrer");
    }
  };

  const featured = useMemo(() => (data?.offers || []).filter(o => o.featured).slice(0, 6), [data]);

  if (!data && busy) {
    return (<><Header onOpenBrief={() => {}} /><main className="max-w-[1280px] mx-auto px-5 py-12 flex justify-center"><Loader2 className="animate-spin" /></main><Footer /></>);
  }

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[1280px] mx-auto px-5 py-8" data-testid="benefits-page">
        {/* Hero */}
        <section className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] via-[#13294b] to-[#1e40af] text-white p-7 md:p-10 mb-6" data-testid="benefits-hero">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-2">
                <ShieldCheck size={13} /> Newseze Member Benefits · {data?.total_offers || 100}+ partner deals
              </div>
              <h1 className="text-[34px] md:text-[44px] font-black tracking-[-0.03em] leading-[1.04]">
                Every benefit a subscriber could want.<br />
                <span className="text-[var(--gold)]">In one place. Sensational.</span>
              </h1>
              <p className="text-[15px] md:text-[16px] opacity-90 mt-3 leading-snug max-w-[700px]">
                100+ partner offers across 15 categories — built for business pros AND college kids.
                Tax filing, ChatGPT Plus discount, Spotify Student bundle, $1,127/yr insurance savings,
                cash-back, 5G phone plans, AI tools, mental health, $20 first-ride Turo credit.
                <b className="text-[var(--gold)]"> +10 credits </b>per click.
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-[0.14em] font-black">
                <span className="bg-[var(--gold)] text-[#1d1303] rounded-full px-2.5 py-1">{data?.offers.filter(o => o.exclusive).length || 0} Newseze-exclusive</span>
                <span className="bg-white/10 rounded-full px-2.5 py-1">FTC-disclosed</span>
                <span className="bg-white/10 rounded-full px-2.5 py-1">Always free for any subscriber</span>
              </div>
            </div>
            <div className="bg-white text-[var(--ink)] rounded-xl p-5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Your tier</div>
              <div className="text-[26px] font-black mt-1 capitalize">{data?.tier}</div>
              <p className="text-[12px] text-[var(--muted)] mt-1.5">
                {data?.tier === "free"
                  ? "Upgrade to Pro to unlock the premium offers (life insurance, Yieldstreet, financial coaching, more)."
                  : "You see every benefit — premium offers included."}
              </p>
              {data?.tier === "free" && (
                <a href="/?go-pro=1" className="btn primary mt-3 w-full justify-center text-[11px]" data-testid="benefits-upgrade-cta">
                  Go Pro · $9.99/mo <ArrowRight size={12} />
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Audience tabs */}
        <div className="flex items-center gap-1.5 flex-wrap mb-5" data-testid="benefits-audiences">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            return (
              <button key={a.id} onClick={() => setAudience(a.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-black border ${audience === a.id ? "bg-[var(--gold)] text-[#1d1303] border-[var(--gold)]" : "bg-white border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`}
                data-testid={`benefits-audience-${a.id}`}>
                <Icon size={12} /> Built for {a.label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => setExclusiveOnly(!exclusiveOnly)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-black border ${exclusiveOnly ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--ink)]"}`}
              data-testid="benefits-exclusive-toggle">
              <Zap size={11} /> Newseze-exclusive only
            </button>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search partners…"
                className="pl-7 pr-2 py-2 text-[12px] border border-[var(--line)] rounded-full w-[200px] bg-white"
                data-testid="benefits-search" />
            </div>
          </div>
        </div>

        {/* Featured carousel */}
        {featured.length > 0 && !activeCat && !search && (
          <section className="mb-6" data-testid="benefits-featured">
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--gold)] flex items-center gap-1.5 mb-2">
              <Star size={11} /> Most-popular this week
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {featured.map((o) => (
                <button key={o.id} onClick={() => click(o)}
                  className="text-left rounded-xl bg-gradient-to-br from-[#fff7d6] to-[#fde68a] border border-[var(--gold)] p-4 hover:-translate-y-0.5 transition"
                  data-testid={`featured-${o.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[15px] font-black text-[#1d1303]">{o.partner}</span>
                    {o.exclusive && <span className="text-[9px] uppercase tracking-[0.14em] font-black bg-[#1d1303] text-[var(--gold)] rounded px-1.5 py-0.5">Exclusive</span>}
                  </div>
                  <p className="text-[12px] text-[#1d1303] opacity-85 leading-snug">{o.value_prop}</p>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[#7a5b00] mt-2">{o.cta_label} →</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Category nav */}
        <div className="flex items-center gap-1.5 flex-wrap mb-5" data-testid="benefits-categories">
          <button onClick={() => setActiveCat("")}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-black border ${!activeCat ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`}
            data-testid="benefits-cat-all">
            All ({data?.total_offers || 0})
          </button>
          {(data?.categories || []).map((c) => {
            const Icon = ICONS[c.icon] || Shield;
            return (
              <button key={c.id} onClick={() => setActiveCat(c.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-black border ${activeCat === c.id ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--ink)] hover:border-[var(--blue)]"}`}
                data-testid={`benefits-cat-${c.id}`}>
                <Icon size={11} /> {c.label} <span className="opacity-70">({c.count})</span>
              </button>
            );
          })}
        </div>

        {/* Offers grid */}
        {busy ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : !data?.offers?.length ? (
          <div className="text-center py-12 text-[var(--muted)] italic">No partners match those filters yet. Try adjusting.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="benefits-grid">
            {data.offers.map((o) => (
              <div key={o.id} className={`rounded-xl border bg-white p-4 flex flex-col gap-2.5 hover:border-[var(--blue)] transition ${o.unlocked ? "border-[var(--line)]" : "border-dashed border-[var(--line)] opacity-70"}`} data-testid={`benefit-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[14.5px] font-black tracking-[-0.01em] text-[var(--ink)] leading-tight">{o.partner}</div>
                    <div className="flex items-center gap-1 flex-wrap mt-1">
                      {o.badge && <span className="text-[9px] uppercase tracking-[0.14em] font-black bg-[var(--gold)] text-[#1d1303] rounded px-1.5 py-0.5">{o.badge}</span>}
                      {o.exclusive && <span className="text-[9px] uppercase tracking-[0.14em] font-black bg-[#1d1303] text-[var(--gold)] rounded px-1.5 py-0.5">Exclusive</span>}
                    </div>
                  </div>
                  {!o.unlocked && <Lock size={14} className="text-[var(--muted)] shrink-0" />}
                </div>
                <p className="text-[12px] text-[var(--muted)] leading-snug flex-1">{o.value_prop}</p>
                <div className="flex items-center gap-2">
                  {o.unlocked ? (
                    <button onClick={() => click(o)} className="btn primary !text-[11px] flex-1 justify-center" data-testid={`benefit-cta-${o.id}`}>
                      {o.cta_label} <ArrowRight size={10} />
                    </button>
                  ) : (
                    <a href="/?go-pro=1" className="btn gold !text-[11px] flex-1 justify-center" data-testid={`benefit-locked-${o.id}`}>
                      Unlock with Pro
                    </a>
                  )}
                  <span className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--gold)] flex items-center gap-0.5 whitespace-nowrap">
                    <Coins size={10} /> +{data?.click_credit_award || 10}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legal footer */}
        <p className="text-[11.5px] text-[var(--muted)] italic mt-8 max-w-[820px]">
          Newseze surfaces affiliate partner offers and may earn a commission when readers convert.
          We do not quote insurance, perform credit repair, or hold reader funds — every offer routes
          directly to the licensed partner. Newseze is not an insurance broker, credit-repair agency,
          or financial advisor. All discount percentages reflect partner-provided promo codes valid
          at time of catalog update.
        </p>
      </main>
      <Footer />
    </>
  );
}
